<template>
  <v-container fluid>
    <div>
      <div class="error" v-if="error">
        <p>{{ error }}</p>
      </div>

      <div class="field">
        <InputField
          v-model="form.nameParent"
          :labelTitle="$t('dash.patient.parent.form-name')"
          type="name"
          placeholder="Jane"
          margin="25px auto"
        ></InputField>
        <InputField
          v-if="emailInviteEnabled"
          v-model="form.emailParent"
          :labelTitle="$t('dash.patient.parent.form-email')"
          type="email"
          placeholder="jane@doe.com"
          margin="25px auto"
        ></InputField>

        <div class="consent-checkbox"
             v-if="!$store.getters.user.hospital.useCuraeConsent">
          <label for="informedConsent" class="checkbox-container">
            {{ $t("dash.patient.create.confirm-consent-1a") }}
            {{ $t("dash.patient.create.confirm-consent-1b") }} <span class="pink--text">*</span>
            <input
              type="checkbox"
              id="informedConsent"
              v-model="form.consent"
            />
            <span class="checkmark"></span>
          </label>
        </div>

        <div class="consent-checkbox">
          <label for="role" class="checkbox-container">
            {{ $t("dash.patient.parent.form-is-parent") }}
            <input
              style="background-color: #e05484; accent-color: #e05484"
              :disabled="parents.length === 0"
              type="checkbox"
              id="role"
              v-model="form.isParent"
            />
            <span class="checkmark" style="border-color: #e05484"></span>
          </label>
        </div>
        <div v-if="!form.isParent" class="d-flex flex-row w-100">
          <label class="ml-6">
            {{ $t("dash.patient.parent.on-behalf-of") }}
          </label>
          <v-select
            class="flex-grow-1 ml-2"
            :options="onlyParents"
            label="nickName"

            v-model="form.onBehalfOf"
            :select-on-key-codes="[188, 13]"
            margin="25px auto"
          ></v-select>
          </div>
      </div>

      <LoadingButton v-if="loading"></LoadingButton>
      <div v-else class="d-flex flex-row justify-center align-center">
        <Button
          type="submit"
          v-if="emailInviteEnabled"
          width="200"
          :title="$t('dash.patient.parent.invite-by-email')"
          style="margin: 16px 0"
          class="mx-2"
          :disabled="formInvalidEmail"
          @click.native="submitForm('email')"
        ></Button>
        <Button
          type="submit"
          v-if="codeInviteEnabled"
          :disabled="formInvalidCode"
          width="200"
          :title="$t('dash.patient.parent.invite-with-code')"
          style="margin: 16px 0"
          class="mx-2"
          @click.native="submitForm('code')"
        ></Button>
      </div>
    </div>

    <div class="divider"></div>
    <h2>{{ $t("dash.patient.invited") }}</h2>
    <v-list v-if="parentInvites" two-line class="v-list-view">
      <template v-for="(invite) in parentInvites">
        <v-divider :key="`div-${invite.id}`"></v-divider>
        <v-list-item :key="invite.id" class="align-content-start text-align-left">
          <v-list-item-avatar class="col-1">
            <v-chip small v-if="invite.role === 'p'">{{$t('dash.patient.parents')}}</v-chip>
            <v-chip small v-if="invite.role === 'o'">{{$t('dash.patient.others')}}</v-chip>

          </v-list-item-avatar>
          <v-list-item-content>

            <v-list-item-title class="align-content-start align-start">
              {{ invite.name }} <span v-if="invite.email"> &bull; {{ invite.email }} </span>
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ $t("dash.patient.parent.invite-last-update") }} {{ invite.updatedAt | moment('D MMMM YYYY') }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action>
            <v-list-item-action-text v-if="invite.accepted">
              <v-btn text disabled>{{ $t("dash.patient.parent.invite-accepted") }}</v-btn>
            </v-list-item-action-text>
            <template v-if="invite.method === 'code' && !invite.accepted">
              <v-list-item-action-text v-if="currentVisibleCode.id === invite.id && currentVisibleCode.code">
                <v-text-field readonly :value="currentVisibleCode.code"></v-text-field>
              </v-list-item-action-text>
              <v-list-item-action-text v-else
                                       @click.prevent="requestCode(invite.id)">
                <v-btn text>{{ $t("dash.patient.parent.invite-show-code") }}</v-btn>
              </v-list-item-action-text>
            </template>
            <v-list-item-action-text v-if="invite.method === 'email' && !invite.accepted">
              <v-btn text @click="resend(invite)" :disabled="resent.includes(invite.id) || loading"><i
                v-if="resent.includes(invite.id)" class="fa fa-check"></i>{{ $t("dash.patient.parent.resend") }}
              </v-btn>
            </v-list-item-action-text>
          </v-list-item-action>
        </v-list-item>
      </template>
    </v-list>
    <div class="divider"></div>
    <h2>{{ $t("dash.patient.parents-or-other") }}</h2>
    <v-list v-if="parents" two-line class="v-list-view">
      <template v-for="(parent) in parents">
        <v-divider :key="`div-${parent.id}`"></v-divider>
        <v-list-item :key="parent.id" class="align-content-start text-align-left">
          <v-list-item-avatar class="col-1">

            <v-chip small v-if="parent.role === 'p'">{{$t('dash.patient.parents')}}</v-chip>

          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="align-content-start align-start">
              {{ parent.firstName || parent.bio }} <span v-if="parent.email"> &bull; {{ parent.email }} </span>
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ $t("dash.patient.parent.invite-last-update") }} {{ parent.updatedAt | moment('D MMMM YYYY') }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-list>
  </v-container>
</template>

<script>
import {neoparentPatients, userInvites} from "@/api";
import moment from "moment";
import vSelect from "vue-select";

export default {
  name: "neoparent-new-parent",
  components: {vSelect},
  data() {
    return {
      currentVisibleCode: {
        id: null,
        code: null
      },
      user: null,
      emailInviteEnabled: false,
      codeInviteEnabled: false,
      patient: null,
      error: "",
      invites: [],
      form: {
        nameParent: "",
        emailParent: "",
        isParent: true,
        consent: false
      },
      loading: false,
      resent: [],
      parents: [],
    };
  },
  mounted() {
    this.patient = this.$store.getters.patient;
    this.user = this.$store.getters.user;
    if (this.user.hospital.inviteMethods) {
      const methods = this.user.hospital.inviteMethods.split(",")
      //check if code is in the list of methods
      this.codeInviteEnabled = methods.includes('code')
      this.emailInviteEnabled = methods.includes('email')
    } else {
      this.emailInviteEnabled = true;
    }
    const id = new URL(window.location).searchParams.get("patientId");
    if (id) {
      neoparentPatients
        .getPatient(id)
        .then(result => {
          this.patient = result.data.patient;
        })
        .catch(err => {
          this.consoleLog(err);
        });
    }
    userInvites
      .getListForPatient(this.patient.id)
      .then(result => {
        this.invites = result.data.invites.filter(x => !x.activated);
      });
    neoparentPatients.getPatientParents(this.patient.id)
      .then( result => {
        this.parents = result.data.parents
      });

  },
  computed: {
    moment() {
      return moment
    },
    onlyParents() {
      return this.parents.filter(x => x.role === 'p')
    },
    parentInvites() {
      if (this.invites.length > 0) {
        const invites = this.invites;
        return invites.sort((a, b) => {
          return new Date(b.updatedAt) - new Date(a.updatedAt);
        });
      }
      return [];
    },
    formInvalidEmail() {
      if (!this.form.consent && !this.$store.getters.user.hospital.useCuraeConsent) {
        return true
      }
      if (!this.form.nameParent || this.form.nameParent.trim().length === 0) {
        return true
      }
      if (this.form.emailParent) {
        if (this.invites.find(x => x.emailParent?.trim()?.toLowerCase() === this.form.emailParent?.trim()?.toLowerCase())) {
          return true
        }
      }
      return !this.form.emailParent || this.form.emailParent.trim().length === 0;
    },
    formInvalidCode() {
      if (!this.form.consent && !this.$store.getters.user.hospital.useCuraeConsent) {
        return true
      }

      if (!this.form.nameParent || this.form.nameParent.trim().length === 0) {
        return true
      }

      if (!this.form.onBehalfOf && !this.form.isParent) {
        return true
      }

      return false;
    }
  },
  methods: {
    resend(invite) {
      const that = this;
      this.loading = true;
      userInvites
        .refresh(invite.id)
        .then(result => {
          that.loading = false;
          that.resent.push(invite.id);
        });
    },
    requestCode(inviteId) {
      this.loading = true;
      userInvites
        .refresh(inviteId)
        .then(result => {
          //remove the entry with id inviteId from the invites list
          const inviteNdx = this.invites.findIndex(invite => invite.id === inviteId)
          this.invites[inviteNdx].lastUpdate = result.data.lastUpdate;
          this.currentVisibleCode = {
            id: inviteId,
            code: result.data.code
          }
        })
        .catch(err => {
          this.error = err.response.data.message;
          this.consoleLog(err);
        });
      this.loading = false;
    },
    submitForm(method) {
      this.loading = true;

      const data = {
        name: this.form.nameParent,
        email: this.form.emailParent?.toLowerCase()?.trim(),
        role: this.form.isParent ? "p" : "o",
        invitedById: this.form.onBehalfOf ? this.form.onBehalfOf.id : null,
        method: method
      }
      userInvites
        .addInvite(this.patient.id, data)
        .then(result => {
          this.invites.push(result.data);
          this.loading = false;
          window.location.reload();
        })
        .catch(err => {
          this.error = err.response.data.message;
          this.consoleLog(err);
          this.loading = false;
        });
    }

  }
};
</script>

<style scoped lang="scss">
.divider {
  width: 100%;
  height: 1px;
  background-color: #e57378;
  margin: 16px 0;
}

.w-100 {
  width: 100%;
}

.v-list-view {
  align-items: start !important;
  text-align: left !important;
}

</style>
